import {rootLogger} from '@/features/development/services/root-logger';

const logger = rootLogger.getChildLogger('stackTraceUtils');

/**
 * Represents parsed information from a stack trace line
 */
export type StackFrameInfo = {
  functionName: string;
  fileName: string;
  lineNumber?: number;
  columnNumber?: number;
  rawLine: string;
};

/**
 * Interface for caller information
 */
export type CallerInfo = {
  caller: StackFrameInfo | string;
  stack: StackFrameInfo[];
  rawStack: string[];
};

/**
 * Parse a stack trace line into structured information
 * @param trLine The stack trace line to parse
 * @returns Structured information about the stack frame
 */
function parseStackLine(line: string): StackFrameInfo {
  const trLine = line.trim();

  // Default values if parsing fails
  const defaultResult: StackFrameInfo = {
    functionName: 'unknown',
    fileName: 'unknown',
    rawLine: trLine,
  };

  try {
    // Common format: "at FunctionName (file:line:column)"
    const atRegex = /at\s+([^\s(]+)\s*\(([^)]+)\)/;
    const atMatch = atRegex.exec(trLine);

    if (atMatch?.[1] && atMatch?.[2]) {
      const functionName = atMatch[1] ?? 'anonymous';
      const locationPart = atMatch[2];

      // Parse file location with line and column numbers
      const locationRegex = /(.+):(\d+):(\d+)$/;
      const locationMatch = locationRegex.exec(locationPart);

      if (locationMatch?.[1] && locationMatch?.[2] && locationMatch?.[3]) {
        return {
          functionName,
          fileName: locationMatch[1],
          lineNumber: Number.parseInt(locationMatch[2], 10),
          columnNumber: Number.parseInt(locationMatch[3], 10),
          rawLine: trLine,
        };
      }

      return {
        functionName,
        fileName: locationPart,
        rawLine: trLine,
      };
    }

    // Alternative format: "at file:line:column"
    const directLocationRegex = /at\s+(.+):(\d+):(\d+)$/;
    const directLocationMatch = directLocationRegex.exec(trLine);

    if (directLocationMatch?.[1] && directLocationMatch?.[2] && directLocationMatch?.[3]) {
      return {
        functionName: 'anonymous',
        fileName: directLocationMatch[1],
        lineNumber: Number.parseInt(directLocationMatch[2], 10),
        columnNumber: Number.parseInt(directLocationMatch[3], 10),
        rawLine: trLine,
      };
    }
  } catch (parseError) {
    logger.debug('Error parsing stack line', {line: trLine, error: parseError});
  }

  return defaultResult;
}

/**
 * Get detailed stack trace information for debugging
 * @param fileToExclude Name of the current file to exclude from the stack
 * @param skipFrames Number of frames to skip from the top of the stack (default: 1 to skip getCallerInfo itself)
 * @returns An object containing the caller and full stack trace
 */
export function getCallerInfo(fileToExclude = '', skipFrames = 1): CallerInfo {
  try {
    // Create an error to capture the stack trace
    const error = new Error('Stack trace capture');
    const stackLines = error.stack?.split('\n') ?? [];

    // Skip the Error constructor line
    const tracelines = stackLines.slice(1);

    // Filter out irrelevant lines (specified file, node_modules, and skip frames)
    const relevantStack = tracelines
      .slice(skipFrames) // Skip specified number of frames (including this function)
      .filter((line) => {
        // If a file to exclude is specified, filter it out
        const shouldExclude = fileToExclude ? line.includes(fileToExclude) : false;
        return !shouldExclude && !line.includes('node_modules/');
      });

    // Parse each line into structured information
    const parsedStack = relevantStack.map((line) => parseStackLine(line));

    // Get the first relevant line as the primary caller
    const primaryCaller: StackFrameInfo | string =
      parsedStack.length > 0 && parsedStack[0] ? parsedStack[0] : 'Unknown caller';

    return {
      caller: primaryCaller,
      stack: parsedStack,
      rawStack: relevantStack,
    };
  } catch (error) {
    // Fallback in case of any issues with stack trace parsing
    logger.warn('Failed to parse stack trace', {error});
    return {
      caller: 'Unknown caller (stack trace error)',
      stack: [],
      rawStack: [],
    };
  }
}

/**
 * Get a simplified caller name for logging purposes
 * @param skipFrames Number of frames to skip (default: 2 to skip this function and its caller)
 * @returns A string representing the caller function or file
 */
export function getCallerName(skipFrames = 2): string {
  const info = getCallerInfo('', skipFrames);

  if (typeof info.caller === 'string') {
    return info.caller;
  }

  return info.caller.functionName === 'anonymous' ? info.caller.fileName : info.caller.functionName;
}
